import { createContext, Dispatch, SetStateAction } from 'react';

import { Location } from '@atom/hooks/useCurrentLocation';
import {
  AssetCreateAttributesInput,
  AssetRouteDataItem,
} from '@atom/types/asset';
import { InventoryCategoryTree } from '@atom/types/category';
import { Schema } from '@atom/types/schema';

export enum ModalStep {
  TYPE = 'TYPE',
  FOLDER = 'FOLDER',
  LOCATION = 'LOCATION',
  ROUTE = 'ROUTE',
  COST = 'COST',
  ATTRIBUTES = 'ATTRIBUTES',
  NAME = 'NAME',
}

export type RouteDataState = { [title: string]: string | null };
export type RouteOptionsState = { [title: string]: AssetRouteDataItem };

export interface ModalStepType {
  title: string;
  value: ModalStep;
}

interface Context {
  activeStep: ModalStep;
  setActiveStep: Dispatch<SetStateAction<ModalStep>>;
  STEPS_ARRAY: ModalStepType[];
  collapsedGroups: Set<String>;
  setCollapsedGroups: Dispatch<SetStateAction<Set<String>>>;
  schema: Partial<Schema>;
  setSchema: Dispatch<SetStateAction<Schema>>;
  category: Partial<InventoryCategoryTree>;
  setCategory: Dispatch<SetStateAction<InventoryCategoryTree>>;
  location: Partial<Location>;
  setLocation: Dispatch<SetStateAction<Location>>;
  address: string;
  setAddress: Dispatch<SetStateAction<string>>;
  routeData: RouteDataState;
  setRouteData: Dispatch<SetStateAction<RouteDataState>>;
  routeOptions: RouteOptionsState;
  setRouteOptions: Dispatch<SetStateAction<RouteOptionsState>>;
  milePost: number;
  setMilePost: Dispatch<SetStateAction<number>>;
  milePostRanges: number[][];
  setMilePostRanges: Dispatch<SetStateAction<number[][]>>;
  rate: number;
  setRate: Dispatch<SetStateAction<number>>;
  unit: string;
  setUnit: Dispatch<SetStateAction<string>>;
  isStockBased: boolean;
  setIsStockBased: Dispatch<SetStateAction<boolean>>;
  isStartEndReading: boolean;
  setIsStartEndReading: Dispatch<SetStateAction<boolean>>;
  isStartEndCalculated: boolean;
  setIsStartEndCalculated: Dispatch<SetStateAction<boolean>>;
  quantityOnHand: number;
  setQuantityOnHand: Dispatch<SetStateAction<number>>;
  name: string;
  disableName: boolean;
  setName: Dispatch<SetStateAction<string>>;
  attributes: AssetCreateAttributesInput;
  setAttributes: Dispatch<AssetCreateAttributesInput>;
  errorAttributeIds: Set<string>;
  setErrorAttributeIds: Dispatch<Set<string>>;
  openCategories: Set<string>;
  setOpenCategories: Dispatch<Set<string>>;
  locationFromSearch: boolean;
  setLocationFromSearch: Dispatch<SetStateAction<boolean>>;
}

export const initialState: Context = {
  activeStep: ModalStep.TYPE,
  setActiveStep: () => {},
  STEPS_ARRAY: [],
  collapsedGroups: new Set([]),
  setCollapsedGroups: () => {},
  schema: {},
  setSchema: () => {},
  category: {},
  setCategory: () => {},
  location: {},
  setLocation: () => {},
  address: '',
  setAddress: () => {},
  routeData: {},
  setRouteData: () => {},
  routeOptions: {},
  setRouteOptions: () => {},
  milePost: null,
  setMilePost: () => {},
  milePostRanges: [],
  setMilePostRanges: () => {},
  rate: null,
  setRate: () => {},
  unit: '',
  setUnit: () => {},
  isStockBased: false,
  setIsStockBased: () => {},
  isStartEndReading: false,
  setIsStartEndReading: () => {},
  isStartEndCalculated: false,
  setIsStartEndCalculated: () => {},
  quantityOnHand: 0,
  setQuantityOnHand: () => {},
  name: '',
  setName: () => {},
  disableName: false,
  attributes: {},
  setAttributes: () => {},
  errorAttributeIds: new Set([]),
  setErrorAttributeIds: () => {},
  openCategories: new Set([]),
  setOpenCategories: () => {},
  locationFromSearch: false,
  setLocationFromSearch: () => {},
};

const CreateAssetModalContext = createContext<Context>(initialState);

export default CreateAssetModalContext;
